import { createPortal } from 'react-dom';
import { Link2Off, RefreshCw, X } from 'lucide-react';

interface BreakLinkModalProps {
  isOpen: boolean;
  /** If true, the source itinerary still exists → show Revert option.
   *  If false, itinerary is gone → only offer Remove Sync Status. */
  itineraryAvailable: boolean;
  onBreakLink: () => void;
  onRevert: () => void;
  onCancel: () => void;
  isReverting?: boolean;
}

const BreakLinkModal = ({
  isOpen,
  itineraryAvailable,
  onBreakLink,
  onCancel,
  isReverting = false,
}: BreakLinkModalProps) => {
  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="break-link-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 z-10">
        {/* Close button */}
        <button
          onClick={onCancel}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {itineraryAvailable ? (
          /* ── Variant 1: Full modal — itinerary exists ── */
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <RefreshCw className="h-5 w-5 text-blue-600" />
              </div>
              <h2
                id="break-link-modal-title"
                className="text-lg font-semibold text-gray-900"
              >
                Port Schedule is Managed by Official Itinerary
              </h2>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              Your port schedule is automatically kept in sync with the cruise
              line's official itinerary. You can break this link if you need to
              make manual adjustments to your port schedule.
            </p>

            <div className="space-y-3">

              {/* Break Link */}
              <button
                onClick={onBreakLink}
                disabled={isReverting}
                className="w-full flex items-start gap-3 p-4 rounded-xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-300 transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <Link2Off className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900 text-sm">Break Link</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Stop automatic updates. Ports become fully editable. You can
                    re-link at any time from this screen.
                  </p>
                </div>
              </button>

              {/* Cancel */}
              <button
                onClick={onCancel}
                disabled={isReverting}
                className="w-full py-2.5 px-4 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-60"
              >
                Cancel — Keep Current Sync
              </button>
            </div>
          </>
        ) : (
          /* ── Variant 2: Reduced modal — itinerary no longer exists ── */
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Link2Off className="h-5 w-5 text-amber-600" />
              </div>
              <h2
                id="break-link-modal-title"
                className="text-lg font-semibold text-gray-900"
              >
                This Port Was Previously Synced
              </h2>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              This cruise was linked to an official itinerary that is no longer
              in our system. Your port data has been fully preserved and is shown
              below.
            </p>

            <div className="space-y-3">
              {/* Remove Sync Status */}
              <button
                onClick={onBreakLink}
                className="w-full flex items-start gap-3 p-4 rounded-xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-300 transition-all text-left"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <Link2Off className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900 text-sm">
                    Remove Sync Status
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Clear the sync status so you can edit your ports freely.
                  </p>
                </div>
              </button>

              {/* Cancel */}
              <button
                onClick={onCancel}
                className="w-full py-2.5 px-4 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default BreakLinkModal;
